// ---------------------------------------------------------------------------
// Test Mode — team chat demo dataset (internal Discord-like messaging).
//
// Two admin-managed group chats plus three DMs between the demo user and the
// Clean Code Culture people, with enough message variety to exercise replies,
// pins, reactions, attachments and link embeds. Writes are simulated: the
// mockTeamChatWrite handler answers the exact envelope shape each mutation
// unwraps (`res.data.message` / `res.data.conversation`), so clicking around
// in Test Mode never crashes — the dataset itself is rebuilt on every call.
// ---------------------------------------------------------------------------

import type {
  ChatMemberInfo,
  MessagesPage,
  TeamChatMessage,
  TeamChatSearchResult,
  TeamConversation,
  UnreadSummary,
} from '../lib/teamChatApi';
import type { VoiceChannel, VoicePresence } from '../lib/voiceApi';
import type { VoiceCallDetail, VoiceCallSummary } from '../lib/voiceCallsApi';
import { isoDateTime } from './company';
import { buildPeople } from './people';

const ME: ChatMemberInfo = { id: 'demo-user', name: 'Demo User', avatarUrl: null };

function member(personId: string): ChatMemberInfo {
  const person = buildPeople().find((p) => p.id === personId);
  return { id: personId, name: person?.fullName ?? personId, avatarUrl: null };
}

function msg(
  partial: Partial<TeamChatMessage> &
    Pick<TeamChatMessage, 'id' | 'conversationId' | 'author' | 'body' | 'createdAt'>,
): TeamChatMessage {
  return {
    attachments: [],
    embeds: [],
    replyTo: null,
    reactions: [],
    pinnedAt: null,
    editedAt: null,
    deleted: false,
    ...partial,
  };
}

// --- Messages per conversation --------------------------------------------

function generalMessages(): TeamChatMessage[] {
  const ioana = member('p2');
  const mihai = member('p3');
  const elena = member('p4');
  return [
    msg({
      id: 'tcm-g1-1',
      conversationId: 'tc-g1',
      author: ioana,
      body: 'Bună dimineața! Nu uitați de stand-up-ul de la 9:30.',
      createdAt: isoDateTime(-1, 8, 55),
      pinnedAt: isoDateTime(-1, 9, 0),
      reactions: [
        {
          emoji: '👍',
          count: 3,
          mine: true,
          userNames: ['Demo User', 'Mihai Georgescu', 'Elena Dumitrescu'],
        },
      ],
    }),
    msg({
      id: 'tcm-g1-2',
      conversationId: 'tc-g1',
      author: mihai,
      body: 'Am urcat raportul de sprint aici:',
      attachments: [
        {
          key: 'team-chat-uploads/demo/sprint-report.pdf',
          fileName: 'sprint-report.pdf',
          contentType: 'application/pdf',
          size: 245_120,
          downloadUrl: null,
        },
      ],
      createdAt: isoDateTime(-1, 11, 20),
    }),
    msg({
      id: 'tcm-g1-3',
      conversationId: 'tc-g1',
      author: ME,
      body: 'Mersi, Mihai! Îl parcurg azi și revin cu feedback.',
      replyTo: {
        id: 'tcm-g1-2',
        authorName: 'Mihai Georgescu',
        snippet: 'Am urcat raportul de sprint aici:',
        deleted: false,
      },
      createdAt: isoDateTime(-1, 11, 32),
      reactions: [{ emoji: '🙏', count: 1, mine: false, userNames: ['Mihai Georgescu'] }],
    }),
    msg({
      id: 'tcm-g1-4',
      conversationId: 'tc-g1',
      author: elena,
      body: 'Articol bun despre prioritizare: https://example.com/prioritization',
      embeds: [
        {
          url: 'https://example.com/prioritization',
          title: 'How great teams prioritize work',
          description: 'A practical framework for deciding what matters this sprint.',
          imageUrl: null,
          siteName: 'example.com',
        },
      ],
      createdAt: isoDateTime(0, 9, 5),
    }),
    msg({
      id: 'tcm-g1-5',
      conversationId: 'tc-g1',
      author: ioana,
      body: 'Super util 🔥 Îl adaug la resursele echipei.',
      createdAt: isoDateTime(0, 9, 12),
    }),
  ];
}

function managementMessages(): TeamChatMessage[] {
  const ioana = member('p2');
  return [
    msg({
      id: 'tcm-g2-1',
      conversationId: 'tc-g2',
      author: ME,
      body: 'Recap: bugetul Q4 se aprobă vineri. Vă rog cifrele finale până joi.',
      createdAt: isoDateTime(-2, 15, 0),
      pinnedAt: isoDateTime(-2, 15, 5),
    }),
    msg({
      id: 'tcm-g2-2',
      conversationId: 'tc-g2',
      author: ioana,
      body: 'Noted — trimit estimările de marketing mâine dimineață.',
      createdAt: isoDateTime(-2, 15, 40),
      editedAt: isoDateTime(-2, 15, 42),
    }),
  ];
}

function dmIoanaMessages(): TeamChatMessage[] {
  const ioana = member('p2');
  return [
    msg({
      id: 'tcm-d1-1',
      conversationId: 'tc-d1',
      author: ME,
      body: 'Salut! Ai 10 minute azi pentru review-ul campaniei?',
      createdAt: isoDateTime(0, 10, 2),
    }),
    msg({
      id: 'tcm-d1-2',
      conversationId: 'tc-d1',
      author: ioana,
      body: 'Sigur, după 14:00 sunt liberă. Îți trimit invite.',
      createdAt: isoDateTime(0, 10, 15),
      reactions: [{ emoji: '✅', count: 1, mine: true, userNames: ['Demo User'] }],
    }),
  ];
}

function dmMihaiMessages(): TeamChatMessage[] {
  const mihai = member('p3');
  return [
    msg({
      id: 'tcm-d2-1',
      conversationId: 'tc-d2',
      author: mihai,
      body: 'Deployment-ul pe staging a mers fără probleme 🚀',
      createdAt: isoDateTime(-1, 17, 45),
    }),
    msg({
      id: 'tcm-d2-2',
      conversationId: 'tc-d2',
      author: ME,
      body: 'Excelent, mulțumesc! Mâine îl promovăm pe producție.',
      createdAt: isoDateTime(-1, 17, 50),
    }),
  ];
}

function dmElenaMessages(): TeamChatMessage[] {
  const elena = member('p4');
  return [
    msg({
      id: 'tcm-d3-1',
      conversationId: 'tc-d3',
      author: elena,
      body: 'Ți-am lăsat draftul de contract în drive, arunci o privire?',
      createdAt: isoDateTime(-3, 12, 20),
    }),
  ];
}

const MESSAGE_BUILDERS: Record<string, () => TeamChatMessage[]> = {
  'tc-g1': generalMessages,
  'tc-g2': managementMessages,
  'tc-d1': dmIoanaMessages,
  'tc-d2': dmMihaiMessages,
  'tc-d3': dmElenaMessages,
};

function allMessages(): TeamChatMessage[] {
  return Object.values(MESSAGE_BUILDERS).flatMap((build) => build());
}

// --- Conversations ----------------------------------------------------------

const UNREAD: Record<string, number> = { 'tc-g1': 2, 'tc-d1': 1 };

/**
 * Presence in Test Mode: the demo user plus two colleagues are online, so both
 * the ONLINE and OFFLINE sections of the member panel have something in them.
 */
export function buildOnlineUserIds(): string[] {
  return ['demo-user', 'p2', 'p4'];
}

export function buildTeamConversations(): TeamConversation[] {
  const conversations: Array<
    Omit<TeamConversation, 'lastMessageAt' | 'lastMessagePreview' | 'unreadCount' | 'sortOrder'>
  > = [
    {
      id: 'tc-g1',
      type: 'GROUP',
      name: 'General',
      members: [ME, member('p2'), member('p3'), member('p4'), member('p5')],
      createdById: 'demo-user',
      createdAt: isoDateTime(-30, 9),
    },
    {
      id: 'tc-g2',
      type: 'GROUP',
      name: 'Management',
      members: [ME, member('p2'), member('p3')],
      createdById: 'demo-user',
      createdAt: isoDateTime(-30, 9),
    },
    {
      id: 'tc-d1',
      type: 'DM',
      name: null,
      members: [ME, member('p2')],
      createdById: null,
      createdAt: isoDateTime(-20, 9),
    },
    {
      id: 'tc-d2',
      type: 'DM',
      name: null,
      members: [ME, member('p3')],
      createdById: null,
      createdAt: isoDateTime(-15, 9),
    },
    {
      id: 'tc-d3',
      type: 'DM',
      name: null,
      members: [ME, member('p4')],
      createdById: null,
      createdAt: isoDateTime(-10, 9),
    },
  ];
  return conversations
    .map((c) => {
      const messages = MESSAGE_BUILDERS[c.id]?.() ?? [];
      const last = messages[messages.length - 1] ?? null;
      return {
        ...c,
        lastMessageAt: last?.createdAt ?? null,
        lastMessagePreview: last
          ? { authorName: last.author.name, snippet: last.body.slice(0, 80) }
          : null,
        unreadCount: UNREAD[c.id] ?? 0,
        // Nothing has been dragged in Test Mode, so the sidebar shows the
        // recency order below.
        sortOrder: null,
      };
    })
    .sort((a, b) => (b.lastMessageAt ?? '').localeCompare(a.lastMessageAt ?? ''));
}

/** Messages for one conversation, newest first (matches the real endpoint). */
export function buildTeamChatMessages(conversationId: string): MessagesPage {
  const messages = (MESSAGE_BUILDERS[conversationId]?.() ?? []).slice().reverse();
  return { messages, nextCursor: null };
}

export function buildTeamChatPins(conversationId: string): TeamChatMessage[] {
  return (MESSAGE_BUILDERS[conversationId]?.() ?? []).filter((m) => m.pinnedAt !== null);
}

/**
 * Message search over the demo dataset, same shape as the real endpoint and the
 * same two-character floor, so the sidebar behaves identically in Test Mode.
 */
export function buildTeamChatSearch(q: string, conversationId?: string): TeamChatSearchResult[] {
  const needle = q.trim().toLowerCase();
  if (needle.length < 2) return [];
  const conversations = buildTeamConversations();
  return allMessages()
    .filter((m) => !conversationId || m.conversationId === conversationId)
    .filter((m) => m.body.toLowerCase().includes(needle))
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, 30)
    .map((m) => {
      const conversation = conversations.find((c) => c.id === m.conversationId);
      return {
        messageId: m.id,
        conversationId: m.conversationId,
        conversationType: conversation?.type ?? 'GROUP',
        conversationName:
          conversation?.name ??
          conversation?.members.find((member) => member.id !== ME.id)?.name ??
          '',
        authorName: m.author.name,
        snippet: m.body.length > 160 ? `${m.body.slice(0, 159)}…` : m.body,
        createdAt: m.createdAt,
      };
    });
}

/**
 * Voice channels for the demo. They are their own entities now, so they have
 * ids of their own rather than borrowing a conversation's.
 */
export function buildVoiceChannels(): VoiceChannel[] {
  return [
    { id: 'vc-1', name: 'General', createdAt: '2026-09-01T08:00:00.000Z' },
    { id: 'vc-2', name: 'Standup', createdAt: '2026-09-02T08:00:00.000Z' },
  ];
}

/**
 * Presence for the demo: two people already standing in General, one of them
 * sharing a screen, so the sidebar shows what a live channel actually looks
 * like. Joining is disabled in Test Mode — the demo must never open a
 * microphone or a real WebRTC connection.
 */
export function buildVoicePresence(): VoicePresence {
  return {
    enabled: true,
    channels: {
      'vc-1': [
        { userId: 'p2', name: member('p2').name, sharingScreen: false },
        { userId: 'p4', name: member('p4').name, sharingScreen: true },
      ],
      'vc-2': [],
    },
  };
}

/**
 * Call history for the demo: yesterday's standup with a transcript, and a
 * quick two-person call with none, so both row states are on screen. The
 * segments read like a real standup so the transcript page has something to
 * show — they are the demo's, not anyone's.
 */
function demoCalls(): VoiceCallDetail[] {
  const standupStart = isoDateTime(-1, 9, 30);
  const standupEnd = isoDateTime(-1, 9, 47);
  const quickStart = isoDateTime(-3, 14, 5);
  const quickEnd = isoDateTime(-3, 14, 11);
  const at = (base: string, minutes: number, seconds = 0) =>
    new Date(new Date(base).getTime() + (minutes * 60 + seconds) * 1000).toISOString();
  const seg = (
    id: string,
    personId: string,
    base: string,
    minutes: number,
    seconds: number,
    text: string,
  ) => ({
    id,
    userId: personId,
    speakerName: member(personId).name,
    startedAt: at(base, minutes, seconds),
    durationMs: 20_000,
    text,
    createdAt: at(base, minutes, seconds + 22),
  });
  return [
    {
      id: 'call-1',
      channelId: 'vc-2',
      channelName: 'Standup',
      startedAt: standupStart,
      endedAt: standupEnd,
      durationMs: new Date(standupEnd).getTime() - new Date(standupStart).getTime(),
      participants: [
        { userId: 'p1', name: member('p1').name, joinedAt: standupStart, leftAt: standupEnd },
        {
          userId: 'p2',
          name: member('p2').name,
          joinedAt: at(standupStart, 0, 40),
          leftAt: standupEnd,
        },
        {
          userId: 'p4',
          name: member('p4').name,
          joinedAt: at(standupStart, 1, 5),
          leftAt: standupEnd,
        },
      ],
      segmentCount: 6,
      pendingSuggestionCount: 0,
      segments: [
        seg(
          'seg-1',
          'p1',
          standupStart,
          1,
          20,
          'Bun, hai să începem. Ieri am închis oferta pentru Construct Vest și am trimis-o.',
        ),
        seg(
          'seg-2',
          'p1',
          standupStart,
          1,
          42,
          'Azi vreau să trec prin facturile restante și să pregătesc mesajele de reamintire.',
        ),
        seg(
          'seg-3',
          'p2',
          standupStart,
          2,
          10,
          'La mine, integrarea cu banca merge. Mai am de verificat extrasul din ING pe august, apar două plăți nepotrivite.',
        ),
        seg(
          'seg-4',
          'p4',
          standupStart,
          3,
          5,
          'Eu am terminat propunerea pentru Cluj. O trimit azi după ce se uită cineva peste prețuri.',
        ),
        seg(
          'seg-5',
          'p1',
          standupStart,
          3,
          30,
          'Mă uit eu până la prânz. Pune-mi un task ca să nu uit.',
        ),
        seg(
          'seg-6',
          'p2',
          standupStart,
          4,
          2,
          'Perfect. Atunci ne vedem joi pentru cash flow-ul pe următoarele șase luni.',
        ),
      ],
    },
    {
      id: 'call-2',
      channelId: 'vc-1',
      channelName: 'General',
      startedAt: quickStart,
      endedAt: quickEnd,
      durationMs: new Date(quickEnd).getTime() - new Date(quickStart).getTime(),
      participants: [
        { userId: 'p2', name: member('p2').name, joinedAt: quickStart, leftAt: quickEnd },
        {
          userId: 'p4',
          name: member('p4').name,
          joinedAt: at(quickStart, 0, 15),
          leftAt: quickEnd,
        },
      ],
      segmentCount: 0,
      pendingSuggestionCount: 0,
      segments: [],
    },
  ];
}

export function buildVoiceCalls(channelId: string | null): VoiceCallSummary[] {
  return demoCalls()
    .filter((c) => !channelId || c.channelId === channelId)
    .map(({ segments: _segments, ...summary }) => summary);
}

export function buildVoiceCall(callId: string): VoiceCallDetail | null {
  return demoCalls().find((c) => c.id === callId) ?? null;
}

export function buildTeamChatUnread(): UnreadSummary {
  const byConversation = { ...UNREAD };
  return {
    total: Object.values(byConversation).reduce((sum, n) => sum + n, 0),
    byConversation,
  };
}

// --- Simulated writes -------------------------------------------------------

/**
 * Shaped responses for team-chat writes. The mutations unwrap
 * `res.data.message` / `res.data.conversation`, so the generic `{ simulated }`
 * no-op would crash their onSuccess handlers in Test Mode.
 */
export function mockTeamChatWrite(pathname: string, method: string, rawBody: unknown): unknown {
  let body: Record<string, unknown> = {};
  try {
    if (typeof rawBody === 'string') body = JSON.parse(rawBody) as Record<string, unknown>;
  } catch {
    body = {};
  }

  // POST /conversations/dm — return the existing demo DM with that user.
  if (pathname === '/api/team-chat/conversations/dm') {
    const userId = typeof body.userId === 'string' ? body.userId : '';
    const conversations = buildTeamConversations();
    const existing = conversations.find(
      (c) => c.type === 'DM' && c.members.some((m) => m.id === userId),
    );
    return { conversation: existing ?? conversations.find((c) => c.type === 'DM') };
  }

  // POST /conversations (group create) — land on an existing demo group.
  if (pathname === '/api/team-chat/conversations' && method === 'POST') {
    return { conversation: buildTeamConversations().find((c) => c.type === 'GROUP') };
  }

  // POST /conversations/reorder — matched before /conversations/:id below, which
  // would otherwise claim it. The demo list is static, so the drag shows via the
  // optimistic patch and the next poll puts it back.
  if (pathname === '/api/team-chat/conversations/reorder') {
    return { reordered: true };
  }

  // PATCH/DELETE /conversations/:id
  const convMatch = pathname.match(/^\/api\/team-chat\/conversations\/([^/]+)$/);
  if (convMatch) {
    if (method === 'DELETE') return { deleted: true };
    return { conversation: buildTeamConversations().find((c) => c.id === convMatch[1]) ?? null };
  }

  // POST /conversations/:id/read
  if (/^\/api\/team-chat\/conversations\/[^/]+\/read$/.test(pathname)) {
    return { read: true };
  }

  // POST /conversations/:id/messages — echo a synthetic message so the
  // optimistic append works; the next poll rebuilds the static list.
  const sendMatch = pathname.match(/^\/api\/team-chat\/conversations\/([^/]+)\/messages$/);
  if (sendMatch) {
    const message: TeamChatMessage = {
      id: `tcm-local-${Date.now()}`,
      conversationId: sendMatch[1],
      author: ME,
      body: typeof body.body === 'string' ? body.body : '',
      attachments: [],
      embeds: [],
      replyTo: null,
      reactions: [],
      pinnedAt: null,
      editedAt: null,
      deleted: false,
      createdAt: new Date().toISOString(),
    };
    return { message };
  }

  // POST /attachments — pretend the upload staged successfully.
  if (pathname === '/api/team-chat/attachments') {
    const dataBase64 = typeof body.dataBase64 === 'string' ? body.dataBase64 : '';
    return {
      key: `team-chat-uploads/demo/${Date.now()}`,
      fileName: typeof body.fileName === 'string' ? body.fileName : 'file',
      contentType:
        typeof body.contentType === 'string' ? body.contentType : 'application/octet-stream',
      size: Math.floor((dataBase64.length * 3) / 4),
    };
  }

  // Message-level writes (edit/delete/pin/unpin/react) — answer with the
  // stored message, lightly transformed so the UI feedback looks right.
  const msgMatch = pathname.match(/^\/api\/team-chat\/messages\/([^/]+)(\/pin|\/reactions)?$/);
  if (msgMatch) {
    const found = allMessages().find((m) => m.id === msgMatch[1]);
    if (!found) return { message: null };
    const message = { ...found };
    if (msgMatch[2] === '/pin') {
      message.pinnedAt = method === 'DELETE' ? null : new Date().toISOString();
    } else if (msgMatch[2] === '/reactions') {
      const emoji = typeof body.emoji === 'string' ? body.emoji : '👍';
      const existing = message.reactions.find((r) => r.emoji === emoji);
      message.reactions = existing
        ? message.reactions
            .map((r) =>
              r.emoji === emoji
                ? { ...r, mine: !r.mine, count: r.mine ? r.count - 1 : r.count + 1 }
                : r,
            )
            .filter((r) => r.count > 0)
        : [...message.reactions, { emoji, count: 1, mine: true, userNames: ['Demo User'] }];
    } else if (method === 'PATCH') {
      message.body = typeof body.body === 'string' ? body.body : message.body;
      message.editedAt = new Date().toISOString();
    } else if (method === 'DELETE') {
      message.deleted = true;
      message.body = '';
      message.attachments = [];
      message.embeds = [];
    }
    return { message };
  }

  return { simulated: true };
}
